import type React from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { motion, AnimatePresence } from "motion/react"
import {
  ArrowUpRight,
  Award04,
  CalendarCheck01,
  Certificate01,
  CheckSquare,
  ChevronDown,
  Coins03,
  Flag05,
  Rocket02,
  Stars02,
  Target04,
  Trophy01,
  Users01,
  Wallet01,
} from "@untitledui/icons"

import { teamsApi } from "@/lib/api/teams"
import { useTeam, useTeamMembers } from "@/lib/api/use-teams"
import { queryKeys } from "@/lib/api/query-keys"
import { ApiError } from "@/lib/api/client"
import type {
  CompetitionModuleConfig,
  CompetitionModuleKey,
  DealershipGoalsRewards,
  DealershipLeaderboardBundle,
  DealershipRewardBundle,
  DealershipRewardTrigger,
  PayoutCadence,
  ServiceLaneBaseline,
  TeamSettingsMetadata,
} from "@/lib/api/types"
import {
  formatMoney,
  formatMoneyExact,
  projectBaseline,
  projectGoalValue,
} from "@/lib/service-lane/baseline-math"
import {
  COMPETITION_DEFAULTS,
  DEFAULT_PAYOUT_CADENCE,
  DEFAULT_PAYOUT_MODE,
  withGoalsRewardsDefaults,
  type CompetitionCadence,
} from "@/lib/dashboard/defaults"
import { Roles } from "@/lib/team/roles"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { useAuth } from "@/lib/auth/auth-context"
import { cx } from "@/lib/utils/cx"

// Pay & Rewards — dealership-side. ServiceManager-owned.
//
// Reward model is *event-triggered*: each trigger fires automatically at a
// specific behavioral moment, and the bundle attached to that trigger is
// what the advisor earns. Trigger metadata lives in TRIGGER_REGISTRY below
// — single source of truth.
//
// Two tiers in the UI:
//   • PAY MODEL (core, always shown) — per-estimate-submitted and
//     per-estimate-completed. These ship ON by default and are the
//     foundation of the platform's promise: every estimate pays.
//   • BONUSES (opt-in, collapsed by default) — activation milestones,
//     weekly/monthly goal hits, 4-week streak, leaderboard. Manager
//     turns these on if they want to layer in gamification. Goal-target
//     inputs live inside the bonus they belong to — turn off the bonus,
//     the goal-progress UI disappears from the dashboard too.

type SectionKey = "payModel" | "bonuses" | "competitions"

type RewardBundleForm = {
  badge: { enabled: boolean; label: string }
  monetary: { enabled: boolean; amount: string }
}

// One form shape for every competition module — irrelevant extras just sit
// unused so switching a module's toggle never has to invent fields. Only
// the fields that matter for a given key make it into the payload.
type CompetitionForm = {
  enabled: boolean
  amount: string
  threshold: string
  streakDays: 5 | 10 | 30
  qualifier: "top_1" | "top_3" | "above_target"
  window: "weekly" | "monthly"
}

type LeaderboardForm = {
  scope: "top_1" | "top_3" | "exceed_goal_pct"
  exceedGoalPct: string
  badge: { enabled: boolean; label: string }
  monetary: { enabled: boolean; amount: string }
}

type FormState = {
  weeklyEstimateTarget: string
  monthlyEstimateTarget: string
  perAdvisorGoals: Record<string, { weekly: string; monthly: string }>
  rewards: Record<DealershipRewardTrigger, RewardBundleForm>
  leaderboard: LeaderboardForm
  payoutCadence: PayoutCadence
  competitions: Record<CompetitionModuleKey, CompetitionForm>
  payoutMode: "manual" | "auto"
}

const EMPTY_BUNDLE: RewardBundleForm = {
  badge: { enabled: false, label: "" },
  monetary: { enabled: false, amount: "" },
}

const ALL_TRIGGERS: DealershipRewardTrigger[] = [
  "perEstimateSubmitted",
  "perEstimateCompleted",
  "activationFirstEstimate",
  "activation5DayStreak",
  "activation30DayStreak",
  "weeklyGoalHit",
  "monthlyGoalHit",
  "weeklyStreak4Bonus",
]

const BONUS_TRIGGERS: DealershipRewardTrigger[] = [
  "activationFirstEstimate",
  "activation5DayStreak",
  "activation30DayStreak",
  "weeklyGoalHit",
  "monthlyGoalHit",
  "weeklyStreak4Bonus",
]

const EMPTY_REWARDS = (): Record<DealershipRewardTrigger, RewardBundleForm> => {
  const out = {} as Record<DealershipRewardTrigger, RewardBundleForm>
  for (const t of ALL_TRIGGERS) out[t] = { ...EMPTY_BUNDLE }
  return out
}

// Render order inside the competitions card — daily / weekly / streak+team
// groups, separated by rules.
const DAILY_COMPETITIONS: CompetitionModuleKey[] = [
  "dailyScanChampion",
  "firstScanOfDay",
  "hotStreak",
  "fastStart",
]
const WEEKLY_COMPETITIONS: CompetitionModuleKey[] = [
  "weeklyVolumeLeaderboard",
  "comebackChallenge",
  "beatPersonalBest",
]
const STREAK_TEAM_COMPETITIONS: CompetitionModuleKey[] = ["streakBonus", "teamGoal"]

const ALL_COMPETITIONS: CompetitionModuleKey[] = [
  ...DAILY_COMPETITIONS,
  ...WEEKLY_COMPETITIONS,
  ...STREAK_TEAM_COMPETITIONS,
]

function hydrateCompetition(
  key: CompetitionModuleKey,
  c: CompetitionModuleConfig | undefined,
): CompetitionForm {
  const d = COMPETITION_DEFAULTS[key]
  return {
    enabled: c?.enabled ?? false,
    amount: c?.amount != null ? String(c.amount) : String(d.defaultAmount),
    threshold:
      c?.threshold != null
        ? String(c.threshold)
        : d.defaultThreshold != null
          ? String(d.defaultThreshold)
          : "",
    streakDays: c?.streakDays ?? d.defaultStreakDays ?? 5,
    qualifier: c?.qualifier ?? d.defaultQualifier ?? "top_1",
    window: c?.window ?? d.defaultWindow ?? "weekly",
  }
}

const EMPTY_COMPETITIONS = (): Record<CompetitionModuleKey, CompetitionForm> => {
  const out = {} as Record<CompetitionModuleKey, CompetitionForm>
  for (const k of ALL_COMPETITIONS) out[k] = hydrateCompetition(k, undefined)
  return out
}

const EMPTY_FORM: FormState = {
  weeklyEstimateTarget: "",
  monthlyEstimateTarget: "",
  perAdvisorGoals: {},
  rewards: EMPTY_REWARDS(),
  leaderboard: {
    scope: "top_1",
    exceedGoalPct: "",
    badge: { enabled: false, label: "" },
    monetary: { enabled: false, amount: "" },
  },
  payoutCadence: DEFAULT_PAYOUT_CADENCE,
  competitions: EMPTY_COMPETITIONS(),
  payoutMode: DEFAULT_PAYOUT_MODE,
}

// Central registry for reward triggers — what fires when, default badge name,
// icon, tone, default monetary. The UI iterates over this; downstream
// services (email templates, advisor dashboard) read the same registry.
interface TriggerMeta {
  title: string
  triggerCopy: string
  defaultBadgeLabel: string
  defaultMonetary: number
  // Whether the badge defaults on. Recurring goal-hit triggers default to
  // money only (no participation-trophy badge); rare milestone triggers
  // get a badge by default since they're actually notable.
  badgeOnByDefault: boolean
  icon: React.ComponentType<{ className?: string }>
  tone: "sky" | "indigo" | "emerald" | "amber"
  cadence:
    | "per-event"
    | "once-per-advisor"
    | "every-week"
    | "every-month"
    | "streak"
}

const TRIGGER_REGISTRY: Record<DealershipRewardTrigger, TriggerMeta> = {
  perEstimateSubmitted: {
    title: "Per estimate submitted",
    triggerCopy:
      "Fires every time an advisor saves an estimate. The advisor's guaranteed slice — habit fuel that doesn't depend on a conversion.",
    defaultBadgeLabel: "Estimate submitted",
    defaultMonetary: 5,
    badgeOnByDefault: false,
    icon: CheckSquare,
    tone: "emerald",
    cadence: "per-event",
  },
  perEstimateCompleted: {
    title: "Per estimate paid",
    triggerCopy:
      "Fires when the body shop transitions an estimate to Completed. Ties advisor pay directly to revenue captured.",
    defaultBadgeLabel: "Estimate paid",
    defaultMonetary: 50,
    badgeOnByDefault: false,
    icon: Wallet01,
    tone: "amber",
    cadence: "per-event",
  },
  activationFirstEstimate: {
    title: "First estimate ever",
    triggerCopy:
      "Fires when an advisor submits their first estimate. The on-the-board moment.",
    defaultBadgeLabel: "On the board",
    defaultMonetary: 5,
    badgeOnByDefault: true,
    icon: Rocket02,
    tone: "sky",
    cadence: "once-per-advisor",
  },
  activation5DayStreak: {
    title: "5-day milestone",
    triggerCopy:
      "Fires the first time an advisor writes ≥1 estimate every workday for a full week.",
    defaultBadgeLabel: "5-day milestone",
    defaultMonetary: 25,
    badgeOnByDefault: true,
    icon: Stars02,
    tone: "sky",
    cadence: "once-per-advisor",
  },
  activation30DayStreak: {
    title: "30-day milestone",
    triggerCopy:
      "Fires after 30 consecutive workdays with ≥1 estimate.",
    defaultBadgeLabel: "30-day milestone",
    defaultMonetary: 100,
    badgeOnByDefault: true,
    icon: Award04,
    tone: "indigo",
    cadence: "once-per-advisor",
  },
  weeklyGoalHit: {
    title: "Hit your weekly goal",
    triggerCopy:
      "Fires every week an advisor's submitted count reaches their personal weekly target.",
    defaultBadgeLabel: "Weekly goal",
    defaultMonetary: 10,
    badgeOnByDefault: false,
    icon: Target04,
    tone: "emerald",
    cadence: "every-week",
  },
  monthlyGoalHit: {
    title: "Hit your monthly goal",
    triggerCopy:
      "Fires once per month if an advisor's monthly count reaches their personal monthly target.",
    defaultBadgeLabel: "Monthly goal",
    defaultMonetary: 50,
    badgeOnByDefault: false,
    icon: Trophy01,
    tone: "emerald",
    cadence: "every-month",
  },
  weeklyStreak4Bonus: {
    title: "4-week run bonus",
    triggerCopy:
      "Fires when an advisor hits their weekly goal 4 consecutive weeks.",
    defaultBadgeLabel: "4-week run",
    defaultMonetary: 50,
    badgeOnByDefault: false,
    icon: CalendarCheck01,
    tone: "indigo",
    cadence: "streak",
  },
}

const SCOPE_OPTIONS: { value: LeaderboardForm["scope"]; label: string; sub: string }[] = [
  { value: "top_1", label: "Top 1", sub: "Only the #1 advisor each month" },
  { value: "top_3", label: "Top 3", sub: "Top three advisors each earn the reward" },
  { value: "exceed_goal_pct", label: "Beat goal by %", sub: "Anyone exceeding by a margin" },
]

const CADENCE_LABEL: Record<TriggerMeta["cadence"], string> = {
  "per-event": "PER EVENT",
  "once-per-advisor": "ONCE PER ADVISOR",
  "every-week": "WEEKLY",
  "every-month": "MONTHLY",
  streak: "STREAK",
}

// UI-only meta for competition modules — titles/copy/defaults live in
// COMPETITION_DEFAULTS (shared with the advisor dashboard); icon + tone
// are presentation concerns that stay here.
const COMPETITION_UI: Record<
  CompetitionModuleKey,
  { icon: React.ComponentType<{ className?: string }>; tone: "sky" | "indigo" | "emerald" | "amber" }
> = {
  dailyScanChampion: { icon: Trophy01, tone: "sky" },
  firstScanOfDay: { icon: Flag05, tone: "sky" },
  hotStreak: { icon: Stars02, tone: "sky" },
  fastStart: { icon: Rocket02, tone: "sky" },
  weeklyVolumeLeaderboard: { icon: Award04, tone: "indigo" },
  comebackChallenge: { icon: ArrowUpRight, tone: "indigo" },
  beatPersonalBest: { icon: Certificate01, tone: "indigo" },
  streakBonus: { icon: CalendarCheck01, tone: "emerald" },
  teamGoal: { icon: Users01, tone: "amber" },
}

const COMPETITION_CADENCE_PILL: Record<
  CompetitionCadence,
  { label: string; tone: keyof typeof PILL_TONE }
> = {
  daily: { label: "DAILY", tone: "sky" },
  weekly: { label: "WEEKLY", tone: "indigo" },
  streak: { label: "STREAK", tone: "emerald" },
  team: { label: "TEAM", tone: "amber" },
}

const QUALIFIER_OPTIONS: { value: CompetitionForm["qualifier"]; label: string; sub: string }[] = [
  { value: "top_1", label: "Top 1", sub: "Only the #1 scanner each week" },
  { value: "top_3", label: "Top 3", sub: "Top three each earn the prize" },
  { value: "above_target", label: "Above target", sub: "Anyone above the weekly target" },
]

function parseNum(v: string): number {
  if (!v.trim()) return 0
  const n = Number.parseFloat(v)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

function hydrateBundle(b: DealershipRewardBundle | undefined): RewardBundleForm {
  return {
    badge: { enabled: b?.badge?.enabled ?? false, label: b?.badge?.label ?? "" },
    monetary: {
      enabled: b?.monetary?.enabled ?? false,
      amount: b?.monetary?.amount != null ? String(b.monetary.amount) : "",
    },
  }
}

function hydrateLeaderboard(l: DealershipLeaderboardBundle | undefined): LeaderboardForm {
  return {
    scope: l?.scope ?? "top_1",
    exceedGoalPct: l?.exceedGoalPct != null ? String(l.exceedGoalPct) : "",
    badge: { enabled: l?.badge?.enabled ?? false, label: l?.badge?.label ?? "" },
    monetary: {
      enabled: l?.monetary?.enabled ?? false,
      amount: l?.monetary?.amount != null ? String(l.monetary.amount) : "",
    },
  }
}

function hydratePerAdvisorGoals(
  g:
    | Record<string, { weeklyEstimateTarget?: number; monthlyEstimateTarget?: number }>
    | undefined,
): Record<string, { weekly: string; monthly: string }> {
  const out: Record<string, { weekly: string; monthly: string }> = {}
  if (!g) return out
  for (const [id, v] of Object.entries(g)) {
    out[id] = {
      weekly: v.weeklyEstimateTarget != null ? String(v.weeklyEstimateTarget) : "",
      monthly: v.monthlyEstimateTarget != null ? String(v.monthlyEstimateTarget) : "",
    }
  }
  return out
}

function perAdvisorGoalsToPayload(
  form: Record<string, { weekly: string; monthly: string }>,
): DealershipGoalsRewards["perAdvisorGoals"] {
  const out: Record<
    string,
    { weeklyEstimateTarget?: number; monthlyEstimateTarget?: number }
  > = {}
  for (const [id, v] of Object.entries(form)) {
    const w = parseNum(v.weekly)
    const m = parseNum(v.monthly)
    if (w > 0 || m > 0) {
      out[id] = {}
      if (w > 0) out[id].weeklyEstimateTarget = w
      if (m > 0) out[id].monthlyEstimateTarget = m
    }
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function hydrateForm(g: DealershipGoalsRewards | undefined): FormState {
  if (!g) return EMPTY_FORM
  const rewards = EMPTY_REWARDS()
  for (const t of ALL_TRIGGERS) {
    rewards[t] = hydrateBundle(g.rewards?.[t])
  }
  const competitions = {} as Record<CompetitionModuleKey, CompetitionForm>
  for (const k of ALL_COMPETITIONS) {
    competitions[k] = hydrateCompetition(k, g.competitions?.[k])
  }
  return {
    weeklyEstimateTarget:
      g.weeklyEstimateTarget != null ? String(g.weeklyEstimateTarget) : "",
    monthlyEstimateTarget:
      g.monthlyEstimateTarget != null ? String(g.monthlyEstimateTarget) : "",
    perAdvisorGoals: hydratePerAdvisorGoals(g.perAdvisorGoals),
    rewards,
    leaderboard: hydrateLeaderboard(g.leaderboardReward),
    payoutCadence: g.payoutCadence ?? DEFAULT_PAYOUT_CADENCE,
    competitions,
    payoutMode: g.payoutMode ?? DEFAULT_PAYOUT_MODE,
  }
}

function bundleToPayload(b: RewardBundleForm): DealershipRewardBundle {
  return {
    badge: { enabled: b.badge.enabled, label: b.badge.label.trim() },
    monetary: { enabled: b.monetary.enabled, amount: parseNum(b.monetary.amount) },
  }
}

function formToPayload(form: FormState): DealershipGoalsRewards {
  const rewardsOut: Partial<Record<DealershipRewardTrigger, DealershipRewardBundle>> = {}
  for (const t of ALL_TRIGGERS) {
    const b = form.rewards[t]
    if (b.badge.enabled || b.monetary.enabled) {
      rewardsOut[t] = bundleToPayload(b)
    }
  }
  // Absent key = module disabled, and each enabled module only carries the
  // extras that matter for its key — mirrors the `rewards` convention.
  const competitionsOut: Partial<Record<CompetitionModuleKey, CompetitionModuleConfig>> = {}
  for (const k of ALL_COMPETITIONS) {
    const c = form.competitions[k]
    if (!c.enabled) continue
    const cfg: CompetitionModuleConfig = { enabled: true, amount: parseNum(c.amount) }
    // Only persist a POSITIVE threshold — a cleared field parses to 0, which
    // the engine would read as a met-instantly target. Omitting it lets the
    // registry default apply instead.
    if (k === "hotStreak" || k === "fastStart" || k === "teamGoal") {
      const th = parseNum(c.threshold)
      if (th > 0) cfg.threshold = th
    }
    if (k === "streakBonus") cfg.streakDays = c.streakDays
    if (k === "weeklyVolumeLeaderboard") {
      cfg.qualifier = c.qualifier
      // "Above target" needs its own scan target — without it the engine can
      // never qualify anyone, so the competition silently never pays.
      if (c.qualifier === "above_target") {
        const th = parseNum(c.threshold)
        if (th > 0) cfg.threshold = th
      }
    }
    if (k === "teamGoal") cfg.window = c.window
    competitionsOut[k] = cfg
  }
  return {
    weeklyEstimateTarget: parseNum(form.weeklyEstimateTarget),
    monthlyEstimateTarget: parseNum(form.monthlyEstimateTarget),
    perAdvisorGoals: perAdvisorGoalsToPayload(form.perAdvisorGoals),
    rewards: rewardsOut,
    leaderboardReward: {
      scope: form.leaderboard.scope,
      exceedGoalPct:
        form.leaderboard.scope === "exceed_goal_pct"
          ? parseNum(form.leaderboard.exceedGoalPct)
          : undefined,
      badge: {
        enabled: form.leaderboard.badge.enabled,
        label: form.leaderboard.badge.label.trim(),
      },
      monetary: {
        enabled: form.leaderboard.monetary.enabled,
        amount: parseNum(form.leaderboard.monetary.amount),
      },
    },
    payoutCadence: form.payoutCadence,
    competitions: competitionsOut,
    payoutMode: form.payoutMode,
  }
}

function triggerActive(b: RewardBundleForm): boolean {
  return b.badge.enabled || b.monetary.enabled
}

function leaderboardActive(l: LeaderboardForm): boolean {
  return l.badge.enabled || l.monetary.enabled
}

interface GoalsRewardsSettingsProps {
  teamId: number | string | null | undefined
}

export function GoalsRewardsSettings({ teamId }: GoalsRewardsSettingsProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: team, isLoading } = useTeam(teamId)
  const { data: members } = useTeamMembers(teamId ? Number(teamId) : null)
  // Goals + payouts are a manager decision. Without this, a service advisor
  // could open this panel and lower their own target (F47).
  const role = useCurrentRole()
  const { user } = useAuth()
  const canManage =
    !!user?.admin ||
    role === Roles.Director ||
    role === Roles.GeneralManager ||
    role === Roles.ServiceManager

  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const initialRef = useRef<FormState>(EMPTY_FORM)
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({
    payModel: true,
    bonuses: false,
    competitions: false,
  })

  const baseline: ServiceLaneBaseline | undefined = useMemo(() => {
    const meta = (team?.metadata ?? {}) as TeamSettingsMetadata
    return meta.serviceLane
  }, [team])

  const projection = useMemo(() => projectBaseline(baseline), [baseline])

  const advisorCount = useMemo(() => {
    if (!members) return 0
    return members.filter((m) => m.role === Roles.ServiceAdvisor).length
  }, [members])

  useEffect(() => {
    if (!team) return
    const meta = (team.metadata ?? {}) as TeamSettingsMetadata
    // Pre-fill the form with platform defaults merged over any stored
    // customizations. Manager sees a complete starting state and can either
    // save-as-is or tune. `initialRef` holds the same merged shape so the
    // sticky save bar only appears when the manager actually changes
    // something (not just because defaults differ from server-empty).
    const effective = withGoalsRewardsDefaults(
      meta.goalsRewards,
      meta.serviceLane,
      advisorCount,
    )
    const next = hydrateForm(effective)
    setForm(next)
    initialRef.current = next
  }, [team, advisorCount])

  const weeklyTarget = parseNum(form.weeklyEstimateTarget)
  const monthlyTarget = parseNum(form.monthlyEstimateTarget)
  const weeklyPerAdvisor =
    advisorCount > 0 && weeklyTarget > 0 ? Math.ceil(weeklyTarget / advisorCount) : null
  const monthlyPerAdvisor =
    advisorCount > 0 && monthlyTarget > 0 ? Math.ceil(monthlyTarget / advisorCount) : null
  const suggestedWeekly = projection.hasInputs
    ? Math.round(projection.viableEstimatesPerWeek * 0.8)
    : null
  const suggestedMonthly = projection.hasInputs
    ? Math.round(projection.viableEstimatesPerMonth * 0.8)
    : null
  const weeklyDollarProjection =
    weeklyTarget > 0 ? projectGoalValue(baseline, weeklyTarget) : 0
  const monthlyDollarProjection =
    monthlyTarget > 0 ? projectGoalValue(baseline, monthlyTarget) : 0

  const activationActiveCount = (
    ["activationFirstEstimate", "activation5DayStreak", "activation30DayStreak"] as const
  ).filter((t) => triggerActive(form.rewards[t])).length

  const weeklyHitActive = triggerActive(form.rewards.weeklyGoalHit)
  const monthlyHitActive = triggerActive(form.rewards.monthlyGoalHit)
  const streakActive = triggerActive(form.rewards.weeklyStreak4Bonus)
  const lbActive = leaderboardActive(form.leaderboard)

  // For the summary pill: how many BONUS reward triggers are live (the core
  // pay-model rewards aren't bonuses — they're the baseline pay). Counts
  // what manager has *layered on* via the Bonuses section.
  const liveRewardCount =
    BONUS_TRIGGERS.filter((t) => triggerActive(form.rewards[t])).length +
    (lbActive ? 1 : 0)
  const monetaryRewardCount =
    BONUS_TRIGGERS.filter((t) => form.rewards[t]?.monetary.enabled).length +
    (form.leaderboard.monetary.enabled ? 1 : 0)

  const competitionCount = ALL_COMPETITIONS.filter(
    (k) => form.competitions[k].enabled,
  ).length

  // Core pay-model rate readouts for the summary card.
  const perEstimateAmount = form.rewards.perEstimateSubmitted.monetary.enabled
    ? parseNum(form.rewards.perEstimateSubmitted.monetary.amount)
    : 0
  const perPaidAmount = form.rewards.perEstimateCompleted.monetary.enabled
    ? parseNum(form.rewards.perEstimateCompleted.monetary.amount)
    : 0

  // Whether the team has SAVED any customization (vs. the initial-render
  // platform defaults). Stable across re-renders — only flips on save.
  const managerCustomized = useMemo(() => {
    const meta = (team?.metadata ?? {}) as TeamSettingsMetadata
    return !!meta.goalsRewards
  }, [team])

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialRef.current),
    [form],
  )

  const overallState: "unset" | "partial" | "complete" = (() => {
    const anyGoal = weeklyTarget > 0 || monthlyTarget > 0
    const anyReward =
      activationActiveCount + (weeklyHitActive ? 1 : 0) + (monthlyHitActive ? 1 : 0) +
        (streakActive ? 1 : 0) + (lbActive ? 1 : 0) > 0
    if (anyGoal && anyReward) return "complete"
    if (anyGoal || anyReward) return "partial"
    return "unset"
  })()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!teamId || !team) return
    setSaving(true)
    try {
      await teamsApi.updateSettings(Number(teamId), {
        metadata: { goalsRewards: formToPayload(form) },
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) })
      initialRef.current = form
      toast.success("Goals & rewards saved")
      const returnUrl = searchParams.get("return")
      if (returnUrl) navigate(returnUrl)
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to save goals & rewards"
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const updateBundle = (
    trigger: DealershipRewardTrigger,
    next: RewardBundleForm,
  ) => {
    setForm((f) => ({ ...f, rewards: { ...f.rewards, [trigger]: next } }))
  }

  const updateCompetition = (key: CompetitionModuleKey, next: CompetitionForm) => {
    setForm((f) => ({ ...f, competitions: { ...f.competitions, [key]: next } }))
  }

  if (!teamId) {
    return (
      <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-6">
        <p className="text-sm text-zinc-500">
          Select a dealership from the team switcher to configure goals.
        </p>
      </div>
    )
  }

  if (!canManage) {
    return (
      <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-6">
        <p className="text-sm font-semibold text-zinc-900">
          Goals &amp; rewards are set by your manager
        </p>
        <p className="text-[13px] text-zinc-500 mt-1 leading-snug">
          You'll see your personal target and progress on your dashboard. Only a
          manager can change the shop's goals and payouts.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <SummaryCard
        state={overallState}
        weeklyTarget={weeklyTarget}
        monthlyTarget={monthlyTarget}
        advisorCount={advisorCount}
        baselineSet={projection.hasInputs}
        teamId={teamId}
        managerCustomized={managerCustomized}
        liveBonusCount={liveRewardCount}
        monetaryBonusCount={monetaryRewardCount}
        competitionCount={competitionCount}
        perEstimate={perEstimateAmount}
        perPaid={perPaidAmount}
        payoutCadence={form.payoutCadence}
      />

      {isLoading ? (
        <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-6">
          <p className="text-sm text-zinc-400">Loading…</p>
        </div>
      ) : (
        <div className="space-y-4 pb-28">
          {/* 0. PAY MODEL — the always-on core. Per estimate + per paid +
              payout cadence. Sits at the top so the manager sees the
              advisor's guaranteed pay first, before any of the optional
              bonus layers. */}
          <SectionCard
            icon={Wallet01}
            tone="emerald"
            title="Pay model (core)"
            statusPill={
              <PillStack>
                {triggerActive(form.rewards.perEstimateSubmitted) ? (
                  <Pill tone="emerald">
                    ${parseNum(form.rewards.perEstimateSubmitted.monetary.amount)}/est
                  </Pill>
                ) : (
                  <Pill tone="zinc">No per-est pay</Pill>
                )}
                {triggerActive(form.rewards.perEstimateCompleted) ? (
                  <Pill tone="amber">
                    ${parseNum(form.rewards.perEstimateCompleted.monetary.amount)}/paid
                  </Pill>
                ) : (
                  <Pill tone="zinc">No per-paid pay</Pill>
                )}
              </PillStack>
            }
            preview="Per estimate submitted + per paid estimate (body-shop completed) + payout cadence"
            isOpen={expanded.payModel}
            onToggle={() => setExpanded((e) => ({ ...e, payModel: !e.payModel }))}
          >
            <div className="space-y-4">
              <p className="text-[12.5px] text-zinc-500 leading-relaxed">
                The advisor's guaranteed pay. Every submitted estimate triggers
                the first reward; every estimate completed by the body shop
                triggers the second. These ship on by default — the calculator
                in{" "}
                <Link
                  to={`/settings/dealership/${teamId}/service-lane`}
                  className="text-emerald-700 font-semibold hover:underline"
                >
                  Service lane
                </Link>{" "}
                suggests grounded rate triples.
              </p>
              <MilestoneRow
                trigger="perEstimateSubmitted"
                bundle={form.rewards.perEstimateSubmitted}
                onChange={(next) => updateBundle("perEstimateSubmitted", next)}
              />
              <MilestoneRow
                trigger="perEstimateCompleted"
                bundle={form.rewards.perEstimateCompleted}
                onChange={(next) => updateBundle("perEstimateCompleted", next)}
              />

              {/* Payout cadence — drives the dashboard's wallet split. */}
              <div className="rounded-2xl border border-zinc-200 bg-zinc-50/40 p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className="text-[13.5px] font-semibold text-zinc-900">
                      Payout cadence
                    </p>
                    <p className="text-[11.5px] text-zinc-500 leading-snug mt-0.5">
                      How often advisors are paid. Drives the wallet's available-
                      vs-pending split.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(["weekly", "biweekly", "monthly"] as const).map((c) => {
                    const active = form.payoutCadence === c
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, payoutCadence: c }))}
                        className={cx(
                          "rounded-xl border px-3 py-2 text-left transition-all duration-150",
                          active
                            ? "border-emerald-500 bg-emerald-50/60 shadow-sm"
                            : "border-zinc-200 bg-white hover:border-zinc-300",
                        )}
                      >
                        <p
                          className={cx(
                            "text-[13.5px] font-semibold capitalize",
                            active ? "text-emerald-900" : "text-zinc-900",
                          )}
                        >
                          {c === "biweekly" ? "Bi-weekly" : c}
                        </p>
                      </button>
                    )
                  })}
                </div>

                {/* Payout approval — whether each period needs manager
                    sign-off before advisors get paid. */}
                <div className="mt-4 pt-3 border-t border-zinc-200/70">
                  <p className="text-[13.5px] font-semibold text-zinc-900">
                    Payout approval
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {(
                      [
                        {
                          value: "manual",
                          label: "Manual",
                          sub: "You review and approve each period",
                        },
                        {
                          value: "auto",
                          label: "Automatic",
                          sub: "Periods auto-approve at computed amounts",
                        },
                      ] as const
                    ).map((opt) => {
                      const active = form.payoutMode === opt.value
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() =>
                            setForm((f) => ({ ...f, payoutMode: opt.value }))
                          }
                          className={cx(
                            "rounded-xl border px-3 py-2 text-left transition-all duration-150",
                            active
                              ? "border-emerald-500 bg-emerald-50/60 shadow-sm"
                              : "border-zinc-200 bg-white hover:border-zinc-300",
                          )}
                        >
                          <p
                            className={cx(
                              "text-[13.5px] font-semibold",
                              active ? "text-emerald-900" : "text-zinc-900",
                            )}
                          >
                            {opt.label}
                          </p>
                          <p className="text-[10.5px] text-zinc-500 leading-tight mt-0.5">
                            {opt.sub}
                          </p>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Divider — bonuses are visually separate, the optional layer. */}
          <div className="pt-2 pb-1 flex items-center gap-3">
            <div className="h-px flex-1 bg-zinc-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                Bonuses
              </span>
              <span className="text-[10.5px] font-medium text-zinc-400">
                · optional
              </span>
            </div>
            <div className="h-px flex-1 bg-zinc-200" />
          </div>

          {/* BONUSES — single flat list of every bonus the dealership can
              enable. Each is a row with its own toggle; weekly/monthly
              hit and the leaderboard reveal extra config inline when
              flipped on. Drops the four-section split that made this
              page feel like a tax form. */}
          <SectionCard
            icon={Trophy01}
            tone="amber"
            title="Bonuses (optional)"
            statusPill={
              <PillStack>
                <Pill tone={liveRewardCount > 0 ? "emerald" : "zinc"}>
                  {liveRewardCount > 0 ? `${liveRewardCount} on` : "All off"}
                </Pill>
              </PillStack>
            }
            preview="Activation milestones, goal hits, streak, leaderboard — toggle the ones you want"
            isOpen={expanded.bonuses}
            onToggle={() => setExpanded((e) => ({ ...e, bonuses: !e.bonuses }))}
          >
            <div className="space-y-3">
              <p className="text-[12.5px] text-zinc-500 leading-relaxed">
                Bonuses are layered on top of the pay model — advisors get
                them on top of per-estimate and per-paid pay. Default off so
                you only pay what you choose to pay.
              </p>

              <MilestoneRow
                trigger="activationFirstEstimate"
                bundle={form.rewards.activationFirstEstimate}
                onChange={(next) => updateBundle("activationFirstEstimate", next)}
              />
              <MilestoneRow
                trigger="activation5DayStreak"
                bundle={form.rewards.activation5DayStreak}
                onChange={(next) => updateBundle("activation5DayStreak", next)}
              />
              <MilestoneRow
                trigger="activation30DayStreak"
                bundle={form.rewards.activation30DayStreak}
                onChange={(next) => updateBundle("activation30DayStreak", next)}
              />

              <MilestoneRow
                trigger="weeklyGoalHit"
                bundle={form.rewards.weeklyGoalHit}
                onChange={(next) => updateBundle("weeklyGoalHit", next)}
              />
              {/* Weekly target — a GOAL, not a bonus setting. It drives the
                  advisor's "X of Y this week" display and the leaderboard even
                  when no weekly-hit bonus is attached, so it's decoupled from
                  the bonus toggle (F45) and shows whenever there are advisors. */}
              {advisorCount > 0 && (
                <InlineTargetInput
                  label="Weekly target"
                  unit="estimates / week"
                  value={form.weeklyEstimateTarget}
                  perAdvisor={weeklyPerAdvisor}
                  advisorCount={advisorCount}
                  dollarProjection={weeklyDollarProjection}
                  suggestion={suggestedWeekly}
                  onChange={(v) =>
                    setForm((f) => ({ ...f, weeklyEstimateTarget: v }))
                  }
                />
              )}

              {/* Per-advisor overrides — a different weekly bar for a standout
                  or a ramping advisor. Absent = they use the global ÷ headcount.
                  Persists in goalsRewards.perAdvisorGoals, keyed by user id. */}
              {advisorCount > 0 && members && (
                <div className="rounded-xl border border-zinc-200/70 bg-zinc-50/40 p-3.5">
                  <p className="text-[12.5px] font-semibold text-zinc-800">
                    Per-advisor overrides (optional)
                  </p>
                  <p className="text-[11.5px] text-zinc-500 mt-0.5 mb-2.5 leading-snug">
                    Everyone defaults to {weeklyPerAdvisor ?? "—"} / week. Set a
                    different weekly target for a standout — or a ramping — advisor.
                  </p>
                  <div className="space-y-1.5">
                    {members
                      .filter((m) => m.role === Roles.ServiceAdvisor)
                      .map((m) => {
                        const id = String(m.id)
                        const val = form.perAdvisorGoals[id]?.weekly ?? ""
                        return (
                          <div key={id} className="flex items-center gap-2">
                            <span className="text-[12.5px] text-zinc-700 flex-1 truncate">
                              {m.firstName} {m.lastName}
                            </span>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              value={val}
                              placeholder={
                                weeklyPerAdvisor != null ? String(weeklyPerAdvisor) : "—"
                              }
                              onChange={(e) => {
                                // Clamp to a sane whole number — no negatives,
                                // no "1e9" surprises; empty clears (#71).
                                const weekly =
                                  e.target.value === ""
                                    ? ""
                                    : String(
                                        Math.min(
                                          999,
                                          Math.max(0, Math.floor(Number(e.target.value) || 0)),
                                        ),
                                      )
                                setForm((f) => {
                                  const next = { ...f.perAdvisorGoals }
                                  const monthly = next[id]?.monthly ?? ""
                                  if (weekly === "" && monthly === "") {
                                    // Both cleared → drop the row, no phantom
                                    // empty entries persisted (#72).
                                    delete next[id]
                                  } else {
                                    next[id] = { weekly, monthly }
                                  }
                                  return { ...f, perAdvisorGoals: next }
                                })
                              }}
                              className="w-20 h-8 rounded-lg border border-zinc-200 bg-white px-2 text-[13px] text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
                            />
                            <span className="text-[11px] text-zinc-400 w-12">/ wk</span>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}

              <MilestoneRow
                trigger="monthlyGoalHit"
                bundle={form.rewards.monthlyGoalHit}
                onChange={(next) => updateBundle("monthlyGoalHit", next)}
              />
              {/* Monthly target — a goal, decoupled from the bonus like the
                  weekly one (F45). */}
              {advisorCount > 0 && (
                <InlineTargetInput
                  label="Monthly target"
                  unit="estimates / month"
                  value={form.monthlyEstimateTarget}
                  perAdvisor={monthlyPerAdvisor}
                  advisorCount={advisorCount}
                  dollarProjection={monthlyDollarProjection}
                  suggestion={suggestedMonthly}
                  onChange={(v) =>
                    setForm((f) => ({ ...f, monthlyEstimateTarget: v }))
                  }
                />
              )}

              {/* Per-advisor MONTHLY overrides — the weekly section had this but
                  the monthly one didn't, so a per-advisor monthly goal couldn't
                  be set at all (F46). Mirrors the weekly override above. */}
              {advisorCount > 0 && members && (
                <div className="rounded-xl border border-zinc-200/70 bg-zinc-50/40 p-3.5">
                  <p className="text-[12.5px] font-semibold text-zinc-800">
                    Per-advisor monthly overrides (optional)
                  </p>
                  <p className="text-[11.5px] text-zinc-500 mt-0.5 mb-2.5 leading-snug">
                    Everyone defaults to {monthlyPerAdvisor ?? "—"} / month. Set a
                    different monthly target for a standout — or a ramping — advisor.
                  </p>
                  <div className="space-y-1.5">
                    {members
                      .filter((m) => m.role === Roles.ServiceAdvisor)
                      .map((m) => {
                        const id = String(m.id)
                        const val = form.perAdvisorGoals[id]?.monthly ?? ""
                        return (
                          <div key={id} className="flex items-center gap-2">
                            <span className="text-[12.5px] text-zinc-700 flex-1 truncate">
                              {m.firstName} {m.lastName}
                            </span>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              value={val}
                              placeholder={
                                monthlyPerAdvisor != null ? String(monthlyPerAdvisor) : "—"
                              }
                              onChange={(e) => {
                                // Same clamp + prune as the weekly override
                                // (#71, #72).
                                const monthly =
                                  e.target.value === ""
                                    ? ""
                                    : String(
                                        Math.min(
                                          999,
                                          Math.max(0, Math.floor(Number(e.target.value) || 0)),
                                        ),
                                      )
                                setForm((f) => {
                                  const next = { ...f.perAdvisorGoals }
                                  const weekly = next[id]?.weekly ?? ""
                                  if (weekly === "" && monthly === "") {
                                    delete next[id]
                                  } else {
                                    next[id] = { weekly, monthly }
                                  }
                                  return { ...f, perAdvisorGoals: next }
                                })
                              }}
                              className="w-20 h-8 rounded-lg border border-zinc-200 bg-white px-2 text-[13px] text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
                            />
                            <span className="text-[11px] text-zinc-400 w-12">/ mo</span>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}

              <MilestoneRow
                trigger="weeklyStreak4Bonus"
                bundle={form.rewards.weeklyStreak4Bonus}
                onChange={(next) => updateBundle("weeklyStreak4Bonus", next)}
              />

              {/* Leaderboard — uses its own row since the config is shaped
                  differently from milestone bundles (scope picker). Sits
                  under the bonuses umbrella with the rest. */}
              <LeaderboardRowConfig
                form={form.leaderboard}
                onChange={(next) =>
                  setForm((f) => ({ ...f, leaderboard: next }))
                }
              />
            </div>
          </SectionCard>

          <div className="pt-2 pb-1 flex items-center gap-3">
            <div className="h-px flex-1 bg-zinc-200" />
            <div className="flex items-center gap-1.5">
              <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                Competitions
              </span>
              <span className="text-[10.5px] font-medium text-zinc-400">
                · optional
              </span>
            </div>
            <div className="h-px flex-1 bg-zinc-200" />
          </div>

          {/* COMPETITION MODULES — the third layer. Head-to-head and team
              contests on scan volume ("valid scan" = an estimate record).
              Same flat row-with-toggle grammar as Bonuses, grouped by
              cadence with subtle rules. */}
          <SectionCard
            icon={Coins03}
            tone="sky"
            title="Competition modules (optional)"
            statusPill={
              <PillStack>
                <Pill tone={competitionCount > 0 ? "emerald" : "zinc"}>
                  {competitionCount > 0 ? `${competitionCount} on` : "All off"}
                </Pill>
              </PillStack>
            }
            preview="Daily, weekly, streak and team contests on scan volume — each pays a fixed prize"
            isOpen={expanded.competitions}
            onToggle={() =>
              setExpanded((e) => ({ ...e, competitions: !e.competitions }))
            }
          >
            <div className="space-y-3">
              <p className="text-[12.5px] text-zinc-500 leading-relaxed">
                Competition modules layer on top of the pay model and bonuses.
                Each one is default off — you only pay what you enable.
              </p>

              <CompetitionGroupRule label="Daily" />
              {DAILY_COMPETITIONS.map((k) => (
                <CompetitionRow
                  key={k}
                  moduleKey={k}
                  form={form.competitions[k]}
                  onChange={(next) => updateCompetition(k, next)}
                />
              ))}

              <CompetitionGroupRule label="Weekly" />
              {WEEKLY_COMPETITIONS.map((k) => (
                <CompetitionRow
                  key={k}
                  moduleKey={k}
                  form={form.competitions[k]}
                  onChange={(next) => updateCompetition(k, next)}
                />
              ))}

              <CompetitionGroupRule label="Streak + Team" />
              {STREAK_TEAM_COMPETITIONS.map((k) => (
                <CompetitionRow
                  key={k}
                  moduleKey={k}
                  form={form.competitions[k]}
                  onChange={(next) => updateCompetition(k, next)}
                />
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      <AnimatePresence>
        {isDirty && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:w-[420px] z-30"
          >
            <div className="rounded-2xl border border-zinc-200/70 bg-white/95 backdrop-blur-xl shadow-2xl shadow-zinc-900/15 px-5 py-3 flex items-center gap-3">
              <div className="size-9 rounded-xl bg-emerald-50 grid place-items-center shrink-0">
                <Flag05 className="size-4 text-emerald-600" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-zinc-900">Unsaved changes</p>
                <p className="text-[11.5px] text-zinc-500 truncate">
                  Save to publish the new goals and rewards to your team.
                </p>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="shrink-0 h-10 px-4 rounded-xl bg-zinc-900 hover:bg-black text-white text-[13.5px] font-semibold disabled:opacity-50 transition-colors duration-150 flex items-center gap-1.5"
              >
                {saving ? (
                  <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  "Save changes"
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  )
}

/* ───────────── Components ───────────── */

const TONE_TILE: Record<"emerald" | "indigo" | "amber" | "sky", string> = {
  emerald: "bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-emerald-600/25",
  indigo: "bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-indigo-600/25",
  amber: "bg-gradient-to-br from-amber-500 to-amber-700 shadow-amber-600/25",
  sky: "bg-gradient-to-br from-sky-500 to-sky-700 shadow-sky-600/25",
}

const PILL_TONE: Record<"emerald" | "indigo" | "amber" | "sky" | "zinc", string> = {
  emerald: "bg-emerald-100 text-emerald-800",
  indigo: "bg-indigo-100 text-indigo-800",
  amber: "bg-amber-100 text-amber-900",
  sky: "bg-sky-100 text-sky-900",
  zinc: "bg-zinc-100 text-zinc-500",
}

function Pill({
  tone,
  children,
}: {
  tone: keyof typeof PILL_TONE
  children: React.ReactNode
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wide",
        PILL_TONE[tone],
      )}
    >
      {children}
    </span>
  )
}
function PillStack({ children }: { children: React.ReactNode }) {
  return <div className="inline-flex items-center gap-1.5 flex-wrap">{children}</div>
}

function SummaryCard({
  state,
  weeklyTarget,
  monthlyTarget,
  advisorCount,
  baselineSet,
  teamId,
  managerCustomized,
  liveBonusCount,
  monetaryBonusCount,
  competitionCount,
  perEstimate,
  perPaid,
  payoutCadence,
}: {
  state: "unset" | "partial" | "complete"
  weeklyTarget: number
  monthlyTarget: number
  advisorCount: number
  baselineSet: boolean
  teamId: number | string
  managerCustomized: boolean
  liveBonusCount: number
  monetaryBonusCount: number
  competitionCount: number
  perEstimate: number
  perPaid: number
  payoutCadence: PayoutCadence
}) {
  void weeklyTarget
  void monthlyTarget
  const tone = state === "complete" ? "emerald" : state === "partial" ? "indigo" : "amber"
  const cadenceLabel =
    payoutCadence === "weekly"
      ? "Weekly payouts"
      : payoutCadence === "biweekly"
        ? "Bi-weekly payouts"
        : "Monthly payouts"
  return (
    <div
      className={cx(
        "rounded-2xl border shadow-sm overflow-hidden",
        state === "complete"
          ? "border-emerald-200/70 bg-emerald-50/40"
          : state === "partial"
            ? "border-indigo-200/60 bg-indigo-50/30"
            : "border-amber-200/80 bg-amber-50/40",
      )}
    >
      <div className="px-6 py-5 flex items-start gap-4">
        <div
          className={cx(
            "size-11 rounded-2xl grid place-items-center shrink-0 shadow-md",
            TONE_TILE[tone],
          )}
        >
          <Wallet01 className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-[15px] font-semibold text-zinc-900">Pay &amp; rewards</h3>
            {!managerCustomized ? (
              <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-amber-300/60 bg-amber-50 text-amber-900 text-[10.5px] font-bold uppercase tracking-[0.12em]">
                Platform defaults
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-emerald-300/60 bg-emerald-50 text-emerald-900 text-[10.5px] font-bold uppercase tracking-[0.12em]">
                Customized
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-500 mt-0.5 leading-relaxed">
            {perEstimate > 0 || perPaid > 0 ? (
              <>
                Advisors earn{" "}
                {perEstimate > 0 && (
                  <>
                    <span className="font-semibold text-zinc-900">${perEstimate}</span>
                    {" per estimate"}
                  </>
                )}
                {perEstimate > 0 && perPaid > 0 && <> + </>}
                {perPaid > 0 && (
                  <>
                    <span className="font-semibold text-zinc-900">${perPaid}</span>
                    {" per paid"}
                  </>
                )}
                {". "}
                <span className="font-semibold text-zinc-900">{advisorCount}</span>{" "}
                active advisor{advisorCount === 1 ? "" : "s"} · {cadenceLabel.toLowerCase()}.
              </>
            ) : (
              <>
                Set the per-estimate and per-paid rates below — these are the
                advisor's guaranteed pay. Bonuses are optional add-ons.
              </>
            )}
          </p>

          {/* Bonus count summary — single-line, just the count of enabled
              optional layers. */}
          <div className="flex items-center gap-1.5 mt-2 text-[11.5px] font-semibold text-zinc-600">
            <span className="inline-flex items-center gap-1">
              <span
                className={cx(
                  "size-1.5 rounded-full",
                  liveBonusCount > 0 ? "bg-emerald-500" : "bg-zinc-300",
                )}
                aria-hidden
              />
              <span className="tabular-nums">{liveBonusCount}</span> bonus{liveBonusCount === 1 ? "" : "es"} enabled
            </span>
            {competitionCount > 0 && (
              <span className="inline-flex items-center gap-1">
                <span className="text-zinc-400" aria-hidden>
                  ·
                </span>
                <span className="tabular-nums">{competitionCount}</span> competition{competitionCount === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {!managerCustomized && (
            <p className="text-[11.5px] text-amber-800 mt-2 leading-snug">
              Platform defaults shown. Save to lock these in or tune before saving.
            </p>
          )}
          {!baselineSet && (
            <Link
              to={`/settings/dealership/${teamId}/service-lane`}
              className="inline-flex items-center gap-1 mt-2 text-[12.5px] font-semibold text-emerald-700 hover:text-emerald-800"
            >
              Set your service lane baseline first
              <ArrowUpRight className="size-3.5" aria-hidden />
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

function SectionCard({
  icon: Icon,
  tone,
  title,
  statusPill,
  preview,
  isOpen,
  onToggle,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  tone: "emerald" | "indigo" | "amber" | "sky"
  title: string
  statusPill: React.ReactNode
  preview: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div
      className={cx(
        "rounded-2xl border bg-white shadow-sm overflow-hidden",
        isOpen ? "border-zinc-300/70" : "border-zinc-200/60",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-zinc-50/60 transition-colors duration-100"
      >
        <div
          className={cx(
            "size-10 rounded-xl grid place-items-center shrink-0 shadow-md",
            TONE_TILE[tone],
          )}
        >
          <Icon className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-[15px] font-semibold text-zinc-900">{title}</h4>
            {statusPill}
          </div>
          <p className="text-[12.5px] text-zinc-500 mt-0.5 truncate">{preview}</p>
        </div>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="shrink-0 text-zinc-400"
        >
          <ChevronDown className="size-5" aria-hidden />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-5 pb-5 pt-2 border-t border-zinc-100">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// MilestoneRow — one bonus, one row, one dollar. Manager toggles the
// reward on and types the amount. No badge, no nested cards, no expand —
// money is the only knob, which matches the wallet-first product model.
function MilestoneRow({
  trigger,
  bundle,
  onChange,
}: {
  trigger: DealershipRewardTrigger
  bundle: RewardBundleForm
  onChange: (next: RewardBundleForm) => void
}) {
  const meta = TRIGGER_REGISTRY[trigger]
  const Icon = meta.icon
  const active = triggerActive(bundle)

  const onMasterToggle = (v: boolean) => {
    if (v) {
      onChange({
        badge: { enabled: false, label: bundle.badge.label || meta.defaultBadgeLabel },
        monetary: {
          enabled: true,
          amount:
            bundle.monetary.amount && parseNum(bundle.monetary.amount) > 0
              ? bundle.monetary.amount
              : String(meta.defaultMonetary),
        },
      })
    } else {
      onChange({
        badge: { enabled: false, label: bundle.badge.label },
        monetary: { enabled: false, amount: bundle.monetary.amount },
      })
    }
  }

  return (
    <div
      className={cx(
        "rounded-2xl border transition-all duration-150",
        active
          ? "border-zinc-300/70 bg-white shadow-sm"
          : "border-zinc-200/60 bg-zinc-50/40",
      )}
    >
      <div className="flex items-center gap-3 p-3">
        <div
          className={cx(
            "size-10 rounded-xl grid place-items-center shrink-0 shadow-md",
            active ? TONE_TILE[meta.tone] : "bg-zinc-300",
          )}
        >
          <Icon className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h6
              className={cx(
                "text-[14px] font-semibold",
                active ? "text-zinc-900" : "text-zinc-600",
              )}
            >
              {meta.title}
            </h6>
            <span
              className={cx(
                "text-[9.5px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded",
                active ? PILL_TONE[meta.tone] : "bg-zinc-100 text-zinc-500",
              )}
            >
              {CADENCE_LABEL[meta.cadence]}
            </span>
          </div>
          <p className="text-[11.5px] text-zinc-500 leading-snug mt-0.5">
            {meta.triggerCopy}
          </p>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          <RowMoneyInput
            value={bundle.monetary.amount}
            disabled={!active}
            placeholder={String(meta.defaultMonetary)}
            onChange={(v) =>
              onChange({
                ...bundle,
                monetary: { ...bundle.monetary, amount: v },
              })
            }
          />
          <Toggle enabled={active} onChange={onMasterToggle} />
        </div>
      </div>
    </div>
  )
}

// Inline dollar input for the bonus row. Compact, tabular-nums, fixed
// width so the column of rows lines up. Disabled visually when its
// row's toggle is off.
function RowMoneyInput({
  value,
  disabled,
  placeholder,
  onChange,
}: {
  value: string
  disabled: boolean
  placeholder: string
  onChange: (v: string) => void
}) {
  return (
    <div className="relative w-[88px]">
      <span
        className={cx(
          "absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] font-medium",
          disabled ? "text-zinc-300" : "text-zinc-400",
        )}
      >
        $
      </span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value
          if (v === "" || /^[0-9]+$/.test(v)) onChange(v)
        }}
        onFocus={(e) => e.currentTarget.select()}
        className={cx(
          "w-full h-9 rounded-lg border border-zinc-200 bg-white pl-5 pr-2 text-[13.5px] font-semibold tabular-nums text-right text-zinc-900 outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10",
          disabled && "opacity-40 bg-zinc-50",
        )}
      />
    </div>
  )
}

// SubReward — compact, single-line reward atom used inside the MilestoneRow
// expanded grid. Three of these per row: badge, monetary, points.
function SubReward({
  icon: Icon,
  tone,
  label,
  enabled,
  onToggle,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  tone: "emerald" | "amber" | "sky"
  label: string
  enabled: boolean
  onToggle: (v: boolean) => void
  children: React.ReactNode
}) {
  return (
    <div
      className={cx(
        "rounded-xl border transition-colors duration-150 p-2.5",
        enabled ? "border-zinc-300/70 bg-white" : "border-zinc-200/60 bg-zinc-50/60",
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className={cx(
            "size-6 rounded-md grid place-items-center shrink-0 shadow-sm",
            enabled ? TONE_TILE[tone] : "bg-zinc-300",
          )}
        >
          <Icon className="size-3 text-white" aria-hidden />
        </div>
        <p
          className={cx(
            "text-[11.5px] font-bold uppercase tracking-wide flex-1",
            enabled ? "text-zinc-700" : "text-zinc-400",
          )}
        >
          {label}
        </p>
        <Toggle enabled={enabled} onChange={onToggle} size="sm" />
      </div>
      <div>{children}</div>
    </div>
  )
}

function Toggle({
  enabled,
  onChange,
  size = "md",
}: {
  enabled: boolean
  onChange: (v: boolean) => void
  size?: "sm" | "md"
}) {
  const dims =
    size === "sm"
      ? { track: "h-4 w-7", thumb: "size-3", on: "translate-x-3.5", off: "translate-x-0.5" }
      : { track: "h-5 w-9", thumb: "size-4", on: "translate-x-[18px]", off: "translate-x-0.5" }
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      className={cx(
        "relative inline-flex shrink-0 rounded-full transition-colors duration-150",
        dims.track,
        enabled ? "bg-zinc-900" : "bg-zinc-200",
      )}
    >
      <span
        className={cx(
          "inline-block rounded-full bg-white shadow-sm transition-transform duration-150 mt-0.5",
          dims.thumb,
          enabled ? dims.on : dims.off,
        )}
      />
    </button>
  )
}

function CompactMoneyInput({
  value,
  disabled,
  onChange,
}: {
  value: string
  disabled: boolean
  onChange: (v: string) => void
}) {
  return (
    <div className="relative">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-[12px] font-medium">
        $
      </span>
      <input
        type="text"
        inputMode="decimal"
        pattern="[0-9]*\.?[0-9]*"
        placeholder="0"
        disabled={disabled}
        value={value}
        onChange={(e) => {
          const v = e.target.value
          if (v === "" || /^[0-9]*\.?[0-9]*$/.test(v)) onChange(v)
        }}
        onFocus={(e) => e.currentTarget.select()}
        className="w-full h-9 rounded-lg border border-zinc-200 bg-white pl-6 pr-2 text-[13px] text-zinc-900 placeholder:text-zinc-300 outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 disabled:opacity-40 disabled:bg-zinc-50"
      />
    </div>
  )
}

// Compact target input — slots inline beneath a weekly/monthly goal-hit
// MilestoneRow when that bonus is enabled. Replaces the full-card
// GoalBlock that used to occupy its own cadence section.
function InlineTargetInput({
  label,
  unit,
  value,
  perAdvisor,
  advisorCount,
  dollarProjection,
  suggestion,
  onChange,
}: {
  label: string
  unit: string
  value: string
  perAdvisor: number | null
  advisorCount: number
  dollarProjection: number
  suggestion: number | null
  onChange: (v: string) => void
}) {
  return (
    <div className="ml-2 mr-2 mb-1 -mt-1 rounded-xl border border-emerald-200/60 bg-emerald-50/30 p-3">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[11px] font-bold uppercase tracking-wide text-zinc-600">
            {label}
          </label>
          <div className="flex items-center gap-2 mt-1">
            <div className="relative flex-1 max-w-[140px]">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={suggestion != null ? String(suggestion) : "—"}
                value={value}
                onChange={(e) => {
                  const v = e.target.value
                  if (v === "" || /^[0-9]+$/.test(v)) onChange(v)
                }}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full h-10 rounded-lg border border-zinc-200 bg-white px-3 text-[14px] font-semibold text-zinc-900 outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10"
              />
            </div>
            <span className="text-[11px] font-medium text-zinc-500">{unit}</span>
          </div>
          {suggestion != null && !value && (
            <button
              type="button"
              onClick={() => onChange(String(suggestion))}
              className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 mt-1.5"
            >
              Use suggested {suggestion}
            </button>
          )}
        </div>
        {perAdvisor != null && advisorCount > 0 && (
          <div className="text-right">
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-zinc-500">
              Per advisor
            </p>
            <p className="text-[18px] font-black text-zinc-900 tabular-nums leading-none mt-0.5">
              ~{perAdvisor}
            </p>
            <p className="text-[10.5px] text-zinc-500 leading-tight mt-0.5">
              across {advisorCount} advisor{advisorCount === 1 ? "" : "s"}
            </p>
          </div>
        )}
      </div>
      {dollarProjection > 0 && (
        <p className="text-[11px] text-zinc-500 leading-snug mt-2">
          Projected dealership upside at this target:{" "}
          <span className="font-semibold text-emerald-700">
            {formatMoneyExact(dollarProjection)}
          </span>
        </p>
      )}
    </div>
  )
}

// Leaderboard configurator — sits in the flat bonus list alongside the
// MilestoneRows. Different shape (scope picker + reward pair) but same
// "toggle to enable" pattern.
function LeaderboardRowConfig({
  form,
  onChange,
}: {
  form: LeaderboardForm
  onChange: (next: LeaderboardForm) => void
}) {
  const active = leaderboardActive(form)
  const onMaster = (v: boolean) => {
    if (v) {
      onChange({
        ...form,
        // Badge tracked in form for API back-compat but never enabled —
        // money is the entire reward in this product.
        badge: { ...form.badge, enabled: false },
        monetary: { enabled: true, amount: form.monetary.amount || "100" },
      })
    } else {
      onChange({
        ...form,
        badge: { ...form.badge, enabled: false },
        monetary: { ...form.monetary, enabled: false },
      })
    }
  }
  return (
    <div
      className={cx(
        "rounded-2xl border transition-all duration-150",
        active
          ? "border-zinc-300/70 bg-white shadow-sm"
          : "border-zinc-200/60 bg-zinc-50/40",
      )}
    >
      <div className="flex items-center gap-3 p-3">
        <div
          className={cx(
            "size-10 rounded-xl grid place-items-center shrink-0 shadow-md",
            active ? TONE_TILE.amber : "bg-zinc-300",
          )}
        >
          <Trophy01 className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h6
              className={cx(
                "text-[14px] font-semibold",
                active ? "text-zinc-900" : "text-zinc-600",
              )}
            >
              Monthly leaderboard
            </h6>
            <span
              className={cx(
                "text-[9.5px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded",
                active ? PILL_TONE.amber : "bg-zinc-100 text-zinc-500",
              )}
            >
              MONTHLY
            </span>
          </div>
          <p className="text-[11.5px] text-zinc-500 leading-snug mt-0.5">
            Individual-vs-individual competition. Prize pays out at month end.
          </p>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          <RowMoneyInput
            value={form.monetary.amount}
            disabled={!active}
            placeholder="100"
            onChange={(v) =>
              onChange({
                ...form,
                monetary: { ...form.monetary, amount: v },
              })
            }
          />
          <Toggle enabled={active} onChange={onMaster} />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {active && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-3 pb-3 pt-1 border-t border-zinc-100/80 mt-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Who qualifies
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {SCOPE_OPTIONS.map((opt) => {
                  const isActive = form.scope === opt.value
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => onChange({ ...form, scope: opt.value })}
                      className={cx(
                        "rounded-lg border px-3 py-2 text-left transition-all duration-150",
                        isActive
                          ? "border-amber-500 bg-amber-50/60"
                          : "border-zinc-200 bg-white hover:border-zinc-300",
                      )}
                    >
                      <p
                        className={cx(
                          "text-[12.5px] font-semibold",
                          isActive ? "text-amber-900" : "text-zinc-900",
                        )}
                      >
                        {opt.label}
                      </p>
                      <p className="text-[10.5px] text-zinc-500 leading-tight mt-0.5">
                        {opt.sub}
                      </p>
                    </button>
                  )
                })}
              </div>
              {form.scope === "exceed_goal_pct" && (
                <div className="pt-2">
                  <label className="text-[11.5px] font-semibold text-zinc-700">
                    Exceed monthly goal by
                  </label>
                  <div className="relative mt-1 max-w-[160px]">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="20"
                      value={form.exceedGoalPct}
                      onChange={(e) => {
                        const v = e.target.value
                        if (v === "" || /^[0-9]+$/.test(v)) {
                          onChange({ ...form, exceedGoalPct: v })
                        }
                      }}
                      className="w-full h-9 rounded-lg border border-zinc-200 bg-white px-3 pr-8 text-[13px] text-zinc-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-[12px] font-medium">
                      %
                    </span>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Hairline rule with a tiny cadence label — separates the daily/weekly/
// streak+team groups inside the competitions card without the weight of
// the full section dividers used between cards.
function CompetitionGroupRule({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2.5 pt-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">
        {label}
      </span>
      <div className="h-px flex-1 bg-zinc-200/70" />
    </div>
  )
}

// CompetitionRow — one module, one row, one prize. Same grammar as
// MilestoneRow; modules with extra knobs (threshold, qualifier, streak
// length, window) expand inline when toggled on, like the leaderboard row.
function CompetitionRow({
  moduleKey,
  form,
  onChange,
}: {
  moduleKey: CompetitionModuleKey
  form: CompetitionForm
  onChange: (next: CompetitionForm) => void
}) {
  const d = COMPETITION_DEFAULTS[moduleKey]
  const ui = COMPETITION_UI[moduleKey]
  const pill = COMPETITION_CADENCE_PILL[d.cadence]
  const Icon = ui.icon
  const active = form.enabled

  const hasExtraConfig =
    moduleKey === "hotStreak" ||
    moduleKey === "fastStart" ||
    moduleKey === "weeklyVolumeLeaderboard" ||
    moduleKey === "streakBonus" ||
    moduleKey === "teamGoal"

  const onMaster = (v: boolean) => {
    if (v) {
      onChange({
        ...form,
        enabled: true,
        amount:
          form.amount && parseNum(form.amount) > 0
            ? form.amount
            : String(d.defaultAmount),
      })
    } else {
      onChange({ ...form, enabled: false })
    }
  }

  return (
    <div
      className={cx(
        "rounded-2xl border transition-all duration-150",
        active
          ? "border-zinc-300/70 bg-white shadow-sm"
          : "border-zinc-200/60 bg-zinc-50/40",
      )}
    >
      <div className="flex items-center gap-3 p-3">
        <div
          className={cx(
            "size-10 rounded-xl grid place-items-center shrink-0 shadow-md",
            active ? TONE_TILE[ui.tone] : "bg-zinc-300",
          )}
        >
          <Icon className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h6
              className={cx(
                "text-[14px] font-semibold",
                active ? "text-zinc-900" : "text-zinc-600",
              )}
            >
              {d.title}
            </h6>
            <span
              className={cx(
                "text-[9.5px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded",
                active ? PILL_TONE[pill.tone] : "bg-zinc-100 text-zinc-500",
              )}
            >
              {pill.label}
            </span>
          </div>
          <p className="text-[11.5px] text-zinc-500 leading-snug mt-0.5">
            {d.description}
          </p>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          <RowMoneyInput
            value={form.amount}
            disabled={!active}
            placeholder={String(d.defaultAmount)}
            onChange={(v) => onChange({ ...form, amount: v })}
          />
          <Toggle enabled={active} onChange={onMaster} />
        </div>
      </div>

      {hasExtraConfig && (
        <AnimatePresence initial={false}>
          {active && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              style={{ overflow: "hidden" }}
            >
              <div className="px-3 pb-3 pt-1 border-t border-zinc-100/80 mt-2 space-y-3">
                {moduleKey === "hotStreak" && (
                  <CompetitionNumberField
                    label="Scans required to trigger"
                    suffix="scans in a day"
                    placeholder={String(d.defaultThreshold ?? 5)}
                    value={form.threshold}
                    onChange={(v) => onChange({ ...form, threshold: v })}
                  />
                )}
                {moduleKey === "fastStart" && (
                  <CompetitionNumberField
                    label="First to reach N scans"
                    suffix="scans"
                    placeholder={String(d.defaultThreshold ?? 3)}
                    value={form.threshold}
                    onChange={(v) => onChange({ ...form, threshold: v })}
                  />
                )}
                {moduleKey === "weeklyVolumeLeaderboard" && (
                  <>
                    <CompetitionOptionPicker
                      heading="Who qualifies"
                      options={QUALIFIER_OPTIONS}
                      value={form.qualifier}
                      onChange={(v) => onChange({ ...form, qualifier: v })}
                    />
                    {form.qualifier === "above_target" && (
                      <CompetitionNumberField
                        label="Weekly scan target"
                        suffix="scans/week"
                        placeholder={String(d.defaultThreshold ?? 20)}
                        value={form.threshold}
                        onChange={(v) => onChange({ ...form, threshold: v })}
                      />
                    )}
                  </>
                )}
                {moduleKey === "streakBonus" && (
                  <CompetitionOptionPicker
                    heading="Consecutive workdays"
                    options={([5, 10, 30] as const).map((days) => ({
                      value: days,
                      label: `${days} days`,
                    }))}
                    value={form.streakDays}
                    onChange={(v) => onChange({ ...form, streakDays: v })}
                  />
                )}
                {moduleKey === "teamGoal" && (
                  <>
                    <CompetitionNumberField
                      label="Collective scan target"
                      suffix="scans, whole team"
                      placeholder={String(d.defaultThreshold ?? 100)}
                      value={form.threshold}
                      onChange={(v) => onChange({ ...form, threshold: v })}
                    />
                    <CompetitionOptionPicker
                      heading="Window"
                      options={[
                        { value: "weekly", label: "Weekly" },
                        { value: "monthly", label: "Monthly" },
                      ]}
                      value={form.window}
                      onChange={(v) => onChange({ ...form, window: v })}
                    />
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  )
}

// Compact labelled number input for a competition module's inline config.
function CompetitionNumberField({
  label,
  suffix,
  placeholder,
  value,
  onChange,
}: {
  label: string
  suffix: string
  placeholder: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
        {label}
      </label>
      <div className="flex items-center gap-2 mt-1.5">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            const v = e.target.value
            if (v === "" || /^[0-9]+$/.test(v)) onChange(v)
          }}
          onFocus={(e) => e.currentTarget.select()}
          className="w-[100px] h-9 rounded-lg border border-zinc-200 bg-white px-3 text-[13.5px] font-semibold tabular-nums text-zinc-900 outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10"
        />
        <span className="text-[11px] font-medium text-zinc-500">{suffix}</span>
      </div>
    </div>
  )
}

// Segmented option picker for a module's inline config — mirrors the
// leaderboard scope picker's button-grid pattern.
function CompetitionOptionPicker<T extends string | number>({
  heading,
  options,
  value,
  onChange,
}: {
  heading: string
  options: { value: T; label: string; sub?: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-2">
        {heading}
      </p>
      <div
        className={cx(
          "grid gap-2",
          options.length === 2 ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-3",
        )}
      >
        {options.map((opt) => {
          const isActive = value === opt.value
          return (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cx(
                "rounded-lg border px-3 py-2 text-left transition-all duration-150",
                isActive
                  ? "border-emerald-500 bg-emerald-50/60"
                  : "border-zinc-200 bg-white hover:border-zinc-300",
              )}
            >
              <p
                className={cx(
                  "text-[12.5px] font-semibold",
                  isActive ? "text-emerald-900" : "text-zinc-900",
                )}
              >
                {opt.label}
              </p>
              {opt.sub && (
                <p className="text-[10.5px] text-zinc-500 leading-tight mt-0.5">
                  {opt.sub}
                </p>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function MoneyInput({
  value,
  disabled,
  onChange,
}: {
  value: string
  disabled: boolean
  onChange: (v: string) => void
}) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-medium">
        $
      </span>
      <input
        type="text"
        inputMode="decimal"
        pattern="[0-9]*\.?[0-9]*"
        placeholder="0"
        disabled={disabled}
        value={value}
        onChange={(e) => {
          const v = e.target.value
          if (v === "" || /^[0-9]*\.?[0-9]*$/.test(v)) onChange(v)
        }}
        onFocus={(e) => e.currentTarget.select()}
        className="w-full h-10 rounded-xl border border-zinc-200 bg-white pl-7 pr-3 text-[13.5px] text-zinc-900 placeholder:text-zinc-300 outline-none transition-all duration-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 disabled:opacity-40 disabled:bg-zinc-50"
      />
    </div>
  )
}

